// Smart View: Treffer kommen in Stufe 0 von Close selbst (gleiche Liste wie in Close), angezeigt mit den
// lokalen Daten. Spalten der View, Sortierung (Close-Reihenfolge oder je Spalte), j/k/Enter, „Nächster Lead“.
// Besser als Close: Felder, nach denen die View sortiert oder filtert (bzw. die Arbeitsfelder Follow-UP Datum,
// Score, Bearbeiter, wenn mindestens jeder vierte Treffer sie hat), kommen als Spalte dazu, sofern sie in den Treffern
// unterschiedlich sind. Die Spalte Kontakt fällt weg, wenn sie meist nur den Namen wiederholt.
// Spaltenordnung (Dilsar, 14.10.2026): Name, direkt daneben Status, dann Telefon/E-Mail nur als Symbole, dann der Rest.
// Runde 5 (Agent 5B): Titel mit Smart-View-Wechsler, Bedienleiste mit Sortierung (mehrere Felder, gemerkt je Nutzer, zeigt die Sortierung der Ansicht),
// Spaltenwahl (je Nutzer und Ansicht gemerkt, auch berechnete Spalten wie Anzahl Anrufe), Textsuche in den geladenen Treffern, Markieren mit x.
// Runde 5 (Vorarbeit): zwei Plätze unter dem Listenkopf, `FilterZeile` (Agent 5A) und `Bedienleiste` (Agent 5B). Dazu der
// Filter-Entwurf: hält die Seite einen, kommen Treffer und Zähler aus `filterApi.treffer` (Abfrage-Engine) statt aus der
// gespeicherten Smart View. Ohne Entwurf (Standard) läuft alles wie bisher. Vertrag: components/smartview/listenVertrag.ts.
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiFehler, smartViewsApi, type CustomField, type LeadZeile, type SmartViewKurz, type SmartView as SmartViewTyp } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { filterApi, type Abfrage, type FilterEntwurf } from "../api/filter";
import { Laden, Leer } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { LeadTabelle, type Spalte } from "../components/LeadTabelle";
import { useSpaltenLayout } from "../components/spaltenLayout";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { ListenTitel } from "../components/smartview/BedienleisteWechsler";
import {
  ALIASE_STANDARD,
  kanonisch,
  kopfLang,
  sortKlick,
  sortVonSmartView,
  sortiereZeilen,
  spaltenBereinigen,
  textTrifft,
  type SortEintrag,
} from "../components/smartview/BedienleisteLogik";
import { berechneteZelle } from "../components/smartview/SpaltenZelle";
import { FilterZeile } from "../components/smartview/FilterZeile";
import { BerechnungsZelle } from "../components/liste/BerechnungsZelle";
import { LimitSteuerung } from "../components/liste/LimitSteuerung";
import { limitVon, mitLimit, sortBeschreibung } from "../components/liste/berechnungModell";
import { useListenBerechnung } from "../components/liste/useListenBerechnung";
import { useUngekuerzteZahl } from "../components/liste/useUngekuerzteZahl";
import { gleicheAbfrage } from "../components/smartview/filterModell";
import { ListenKopfRechts } from "../components/smartview/ListenKopfRechts";
import { ListenSchluessel, type ListenSpalte } from "../components/smartview/listenVertrag";
import { leadZelle, wertDerSpalte } from "../components/LeadZelle";
import { KONTAKTWEGE, listenSpalten } from "../components/listenSpalten";
import { listeLesen, listeSetzen } from "../daten/liste";
import { useBerechneteWerte, useFelderKatalog, useGemerkteAnsicht } from "../daten/listenAnsichtStore";
import { useMeta } from "../daten/meta";
import { useSeitenKopf } from "../daten/seite";
import { anheften, useSmartViews, zahlMelden } from "../daten/smartviews";
import { beimNutzerwechsel } from "../daten/sitzung";
import { useNeuVerbunden } from "../daten/verbindung";
import { useAuth } from "../auth/AuthContext";
import { T } from "../texte";
import { istLeer } from "../werkzeuge/felder";

const AUSWAHL = "__auswahl";
const SEITE = 200;
const AUTOMATISCH_BIS = 600; // so viele Treffer werden ohne Klick geladen (Close-Cursor gilt nur ca. 30 s)

interface Stand {
  view: SmartViewTyp | null;
  zeilen: LeadZeile[];
  total: number | null;
  cursor: string | null;
  hatMehr: boolean;
  fehlend: string[];
}

const LEER: Stand = { view: null, zeilen: [], total: null, cursor: null, hatMehr: false, fehlend: [] };
const ablage = new Map<string, Stand>();
// Nach einem Nutzerwechsel keine Treffer der vorherigen Sitzung zeigen.
beimNutzerwechsel(() => ablage.clear());

const STANDARD_SPALTEN: Spalte[] = [
  { key: "display_name", label: "Name" },
  { key: "primary_contact_name", label: "Kontakt" },
  { key: "primary_phone", label: "Telefon" },
  { key: "status_id", label: "Status" },
];

/** Arbeitsfelder in dieser Reihenfolge (gleiche Namen wie links auf der Lead-Seite). */
const ARBEITSFELDER: RegExp[] = [/^follow-?up[ -]?datum$/i, /^(lead|follow-?up)[ -]?score$/i, /^bearbeiter$/i, /^cc[ -]?termin$/i];
/** Höchstens so viele Spalten kommen zu den Spalten der View dazu (bei 1280 px darf nichts seitlich scrollen). */
const ZUSATZ_HOECHSTENS = 2;
/** Arbeitsfelder, nach denen die View nicht filtert, kommen dazu, wenn mindestens jeder vierte Treffer sie hat. */
const ARBEITSFELD_ANTEIL = 0.25;

/** Custom-Field-IDs, nach denen die View sortiert, und danach die, nach denen sie filtert (Close-Abfragebaum). */
function felderDerAbfrage(view: SmartViewTyp | null): string[] {
  const ids: string[] = [];
  const sammeln = (knoten: unknown) => {
    if (Array.isArray(knoten)) knoten.forEach(sammeln);
    else if (knoten && typeof knoten === "object") {
      for (const [k, w] of Object.entries(knoten as Record<string, unknown>)) {
        if (k === "custom_field_id" && typeof w === "string") {
          const id = w.replace(/^custom\./, "");
          if (!ids.includes(id)) ids.push(id);
        } else sammeln(w);
      }
    }
  };
  sammeln(view?.s_query?.sort ?? view?.sort ?? null);
  sammeln(view?.s_query?.query ?? null);
  return ids;
}

function gleicherText(a: string | null | undefined, b: string | null | undefined): boolean {
  const n = (x: string | null | undefined) => (x ?? "").toLocaleLowerCase("de").replace(/\s+/g, " ").trim();
  return !!n(a) && n(a) === n(b);
}

function spaltenKlasse(key: string, feld: CustomField | undefined): string | undefined {
  if (key === KONTAKTWEGE) return "spalte-wege";
  if (key === "display_name" || key === "name") return "spalte-name";
  if (key === "primary_phone") return "spalte-telefon";
  if (key === "primary_email") return "spalte-mail";
  if (key === "status_id" || key === "status_label") return "spalte-status";
  if (key === "date_created" || key === "date_updated" || feld?.type === "date" || feld?.type === "datetime") return "spalte-datum";
  if (feld) return "spalte-feld";
  return undefined;
}

/** Text, in dem die Suche in der Liste sucht: Name, Kontakt, Telefon, Mail, Status und alle Werte der Zeile. */
function zeilenText(z: LeadZeile, statusLabel: string | undefined): string {
  const teile: unknown[] = [z.display_name, z.name, z.primary_contact?.name, z.primary_contact?.phone, z.primary_contact?.email, z.status_label, statusLabel];
  for (const w of [...Object.values(z.values ?? {}), ...Object.values(z.custom ?? {})]) teile.push(Array.isArray(w) ? w.join(" ") : w);
  return teile.filter((x) => x !== null && x !== undefined && typeof x !== "object").join(" ");
}

export default function SmartView() {
  const { viewId = "" } = useParams();
  const meta = useMeta();
  const { nutzer } = useAuth();
  const navigate = useNavigate();
  const meldung = useMeldung();
  const { views: alleViews } = useSmartViews();
  const [stand, setStand] = useState<Stand>(() => ablage.get(viewId) ?? LEER);
  const [laedt, setLaedt] = useState(!ablage.has(viewId));
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fokus, setFokus] = useState(0);
  const [suche, setSuche] = useState("");
  const [markiert, setMarkiert] = useState<Set<string>>(() => new Set());
  // Filter-Entwurf (Agent 5A setzt ihn über die FilterZeile); null = Treffer der gespeicherten Smart View
  const [entwurf, setEntwurf] = useState<FilterEntwurf | null>(null);
  const entwurfRef = useRef(entwurf);
  entwurfRef.current = entwurf;
  const entwurfBisher = useRef<FilterEntwurf | null>(null);
  const generation = useRef(0);
  // zählt jedes fertige Laden der Liste: die Berechnungszeile rechnet dann neu (Werte können sich geändert haben)
  const [ladeStand, setLadeStand] = useState(0);
  const standRef = useRef(stand);
  standRef.current = stand;

  const kurz = alleViews?.find((v) => v.id === viewId) ?? meta.meta?.smart_views.find((v) => v.id === viewId);
  const name = stand.view?.name ?? kurz?.name ?? "Smart View";
  useSeitenKopf([{ label: T.nav.smartViews, to: "/smart-views/alle" }, { label: name }]);
  useEffect(() => {
    if (stand.total !== null && !entwurf) zahlMelden(viewId, stand.total);
  }, [viewId, stand.total, entwurf]);

  // Mit Entwurf nichts in den Zwischenspeicher der gespeicherten Smart View legen
  const setzen = useCallback(
    (s: Stand) => {
      if (!entwurfRef.current) ablage.set(viewId, s);
      setStand(s);
    },
    [viewId],
  );

  /** Treffer holen: aus dem Entwurf (Engine) oder aus der gespeicherten Smart View (Close). */
  const trefferHolen = useCallback(
    (p: { limit: number; cursor?: string | null }) => {
      const e = entwurfRef.current;
      return e ? filterApi.treffer({ s_query: e.s_query, limit: p.limit, cursor: p.cursor }) : smartViewsApi.treffer(viewId, p);
    },
    [viewId],
  );

  const weitere = useCallback(
    async (gen: number, basis: Stand, bis: number): Promise<Stand> => {
      let s = basis;
      while (s.hatMehr && s.cursor && s.zeilen.length < bis && gen === generation.current) {
        const r = await trefferHolen({ limit: SEITE, cursor: s.cursor });
        const bekannt = new Set(s.zeilen.map((z) => z.id));
        s = {
          ...s,
          zeilen: [...s.zeilen, ...r.data.filter((z) => !bekannt.has(z.id))],
          cursor: r.cursor,
          hatMehr: r.has_more,
          total: r.total ?? s.total,
          fehlend: [...s.fehlend, ...(r.missing ?? [])],
        };
        if (gen === generation.current) setzen(s);
      }
      return s;
    },
    [setzen, trefferHolen],
  );

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      setFehler(null);
      try {
        const [v, r] = await Promise.all([smartViewsApi.eine(viewId), trefferHolen({ limit: SEITE })]);
        if (gen !== generation.current) return;
        const s: Stand = { view: v.smart_view, zeilen: r.data, total: r.total, cursor: r.cursor, hatMehr: r.has_more, fehlend: r.missing ?? [] };
        setzen(s);
        setLaedt(false);
        setLadeStand((n) => n + 1);
        // Fokus auf den zuletzt geöffneten Lead zurücksetzen
        const k = listeLesen();
        if (k?.pfad === `/smart-views/${viewId}` && k.fokus) {
          const i = s.zeilen.findIndex((z) => z.id === k.fokus);
          if (i >= 0) setFokus(i);
        }
        await weitere(gen, s, AUTOMATISCH_BIS);
      } catch (e) {
        if (gen !== generation.current) return;
        setLaedt(false);
        setFehler(e);
      }
    },
    [viewId, setzen, weitere, trefferHolen],
  );

  useEffect(() => {
    // andere Smart View: der Entwurf der vorigen gilt nicht mehr (auch nicht für das Laden gleich unten)
    entwurfRef.current = null;
    entwurfBisher.current = null;
    setEntwurf(null);
    const vorhanden = ablage.get(viewId);
    setStand(vorhanden ?? LEER);
    setSuche("");
    setMarkiert(new Set());
    const k = listeLesen();
    const i = vorhanden && k?.pfad === `/smart-views/${viewId}` && k.fokus ? vorhanden.zeilen.findIndex((z) => z.id === k.fokus) : -1;
    setFokus(i >= 0 ? i : 0);
    setLaedt(!vorhanden);
    void laden(!!vorhanden);
  }, [viewId, laden]);

  // Entwurf gesetzt, geändert oder verworfen: Treffer neu holen (die Anzeige bleibt stehen, bis die neuen da sind)
  useEffect(() => {
    if (entwurfBisher.current === entwurf) return;
    entwurfBisher.current = entwurf;
    setMarkiert(new Set());
    setFokus(0);
    void laden(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entwurf]);

  const mehrLaden = useCallback(async () => {
    const s = standRef.current;
    if (!s.hatMehr || laedtMehr) return;
    setLaedtMehr(true);
    try {
      await weitere(generation.current, s, s.zeilen.length + SEITE);
    } catch (e) {
      if (e instanceof ApiFehler && e.code === "cursor_abgelaufen") void laden(true);
      else setFehler(e);
    } finally {
      setLaedtMehr(false);
    }
  }, [laedtMehr, weitere, laden]);

  // Änderungen an Leads dieser Liste (Status, Felder): Liste still neu laden, Position bleibt.
  const neuLadenZeit = useRef<number | undefined>(undefined);
  useEchtzeit(["lead.updated"], (e) => {
    if (!e.lead_id || !standRef.current.zeilen.some((z) => z.id === e.lead_id)) return;
    window.clearTimeout(neuLadenZeit.current);
    neuLadenZeit.current = window.setTimeout(() => void laden(true), 1500);
  });
  useNeuVerbunden(() => void laden(true));
  useEffect(() => () => window.clearTimeout(neuLadenZeit.current), []);
  // Smart View wurde (von jemand anderem oder in einem anderen Fenster) gespeichert oder geändert: Definition und Treffer neu holen
  useEchtzeit(["smart_view.updated"], (e) => {
    if ((e.data as { smart_view_id?: string }).smart_view_id !== viewId || entwurfRef.current) return;
    ablage.delete(viewId);
    void laden(true);
  });

  // Stichprobe für die Spaltenwahl: die erste Seite, damit beim Nachladen keine Spalten springen.
  const probe = useMemo(() => stand.zeilen.slice(0, SEITE), [stand.zeilen]);
  const standardSpalten: Spalte[] = useMemo(() => {
    let roh = stand.view?.columns?.length ? stand.view.columns.map((c) => ({ key: c.key, label: c.label })) : STANDARD_SPALTEN;
    const vorhanden = new Set(roh.map((c) => c.key.replace(/^custom\./, "")));

    // Kontakt weglassen, wenn er bei den meisten Treffern nur den Lead-Namen wiederholt
    if (probe.length && roh.some((c) => c.key === "primary_contact_name")) {
      const mitKontakt = probe.filter((z) => z.primary_contact?.name);
      const gleich = mitKontakt.filter((z) => gleicherText(z.primary_contact?.name, z.display_name ?? z.name)).length;
      if (mitKontakt.length && gleich / mitKontakt.length > 0.5) roh = roh.filter((c) => c.key !== "primary_contact_name");
    }

    // Arbeitsspalten ergänzen: nur Lead-Felder, nur wenn die Treffer darin verschiedene Werte haben
    const unterschiedlich = (id: string, mindestAnteil: number) => {
      const werte = probe.map((z) => z.custom?.[id]).filter((w) => !istLeer(w));
      if (!werte.length || werte.length / probe.length < mindestAnteil) return false;
      return probe.length === 1 || new Set(werte.map((w) => JSON.stringify(w))).size > 1;
    };
    const leadFelder = meta.meta?.custom_fields.lead ?? [];
    const kandidaten: { id: string; anteil: number }[] = [
      ...felderDerAbfrage(stand.view).map((id) => ({ id, anteil: 0 })),
      ...ARBEITSFELDER.flatMap((r) => leadFelder.filter((f) => r.test(f.name.trim())).map((f) => ({ id: f.id, anteil: ARBEITSFELD_ANTEIL }))),
    ];
    const zusatz: { key: string; label: string }[] = [];
    for (const k of kandidaten) {
      if (zusatz.length >= ZUSATZ_HOECHSTENS || !probe.length) break;
      const f = leadFelder.find((x) => x.id === k.id);
      if (!f || vorhanden.has(f.id) || !unterschiedlich(f.id, k.anteil)) continue;
      vorhanden.add(f.id);
      zusatz.push({ key: `custom.${f.id}`, label: f.name });
    }
    // Name, Status, Symbole Telefon/E-Mail vorn; danach die Spalten der View und die Zusatzspalten
    return listenSpalten([...roh, ...zusatz]).map((s) =>
      s.key === KONTAKTWEGE
        ? { ...s, klasse: spaltenKlasse(s.key, undefined), kopfTitel: "Telefon und E-Mail des Hauptkontakts" }
        : { ...s, sortierbar: true, klasse: spaltenKlasse(s.key, meta.feld(s.key)) },
    );
  }, [stand.view, probe, meta]);

  // --- Spaltenwahl (Agent 5B): Katalog der Lead-Spalten plus die Spalten der Ansicht, die der Katalog nicht kennt ---
  const liste = ListenSchluessel.smartView(viewId);
  const katalog = useFelderKatalog();
  const gemerkt = useGemerkteAnsicht(liste);
  const aliase = katalog?.aliase ?? ALIASE_STANDARD;
  const kan = useCallback((k: string) => kanonisch(k, aliase), [aliase]);
  const katalogMap = useMemo(() => new Map((katalog?.felder ?? []).map((f) => [f.key, f])), [katalog]);
  const waehlbar: ListenSpalte[] = useMemo(() => {
    if (!katalog) return [];
    const aus: ListenSpalte[] = katalog.felder.map((f) => ({ key: f.key, label: f.label, gruppe: f.gruppe, sortierbar: f.sortierbar, fest: f.fest }));
    for (const s of standardSpalten) {
      const k = kan(s.key);
      if (!katalogMap.has(k) && k !== KONTAKTWEGE) aus.push({ key: k, label: s.label, gruppe: "Spalten der Ansicht", sortierbar: true });
    }
    return aus;
  }, [katalog, katalogMap, standardSpalten, kan]);
  const waehlbarMap = useMemo(() => new Map(waehlbar.map((w) => [w.key, w])), [waehlbar]);

  const eigeneSpalten = gemerkt.ansicht?.spalten ?? null;
  const spalten: Spalte[] = useMemo(() => {
    if (!eigeneSpalten || !waehlbar.length) return standardSpalten;
    const keys = spaltenBereinigen(eigeneSpalten, aliase, new Set(waehlbar.map((w) => w.key)), ["display_name"]);
    if (!keys.length) return standardSpalten;
    return keys.map((k): Spalte => {
      const std = standardSpalten.find((s) => kan(s.key) === k);
      if (std) return std;
      if (k === KONTAKTWEGE) return { key: k, label: "", klasse: "spalte-wege", kopfTitel: "Telefon und E-Mail des Hauptkontakts" };
      const w = waehlbarMap.get(k);
      const f = katalogMap.get(k);
      const lang = kopfLang(k) ? " spalte-lang" : "";
      const klasse = spaltenKlasse(k, meta.feld(k)) ?? (f ? `${f.typ === "zeit" || f.typ === "datum" ? "spalte-datum" : "spalte-feld"}${lang}` : undefined);
      return { key: k, label: w?.label ?? k, sortierbar: w?.sortierbar !== false, klasse };
    });
  }, [eigeneSpalten, waehlbar, waehlbarMap, katalogMap, standardSpalten, aliase, kan, meta]);
  const gezeigt = useMemo(() => spalten.map((s) => kan(s.key)), [spalten, kan]);
  const standardKeys = useMemo(() => standardSpalten.map((s) => kan(s.key)), [standardSpalten, kan]);

  const spaltenMitAuswahl: Spalte[] = useMemo(() => [{ key: AUSWAHL, label: "", kopfTitel: "Auswahl für Massen-Mail und Massenbearbeitung", klasse: "sv-auswahlspalte" }, ...spalten], [spalten]);

  // --- Sortierung: eigene Wahl (gemerkt je Nutzer und Ansicht, bis zu drei Felder) oder die Reihenfolge der Ansicht (Close) ---
  const eigeneSort: SortEintrag[] = useMemo(() => gemerkt.ansicht?.sortierung ?? [], [gemerkt.ansicht?.sortierung]);
  const ansichtSort: SortEintrag[] = useMemo(
    () => sortVonSmartView(stand.view?.s_query?.sort ?? stand.view?.sort ?? null).map((s) => ({ ...s, key: kan(s.key) })),
    [stand.view, kan],
  );
  const wirksameSort = eigeneSort.length ? eigeneSort : ansichtSort;
  const sortSetzen = useCallback((neu: SortEintrag[]) => gemerkt.aendern({ sortierung: neu.length ? neu : null }), [gemerkt]);
  const spaltenSetzen = useCallback((keys: string[] | null) => gemerkt.aendern({ spalten: keys }), [gemerkt]);
  const layout = useSpaltenLayout(gemerkt, gezeigt, spaltenSetzen);

  // --- Berechnete Spalten (Anzahl Anrufe, letzte Kommunikation …): nur holen, was gezeigt oder sortiert wird und die Zeile nicht selbst trägt ---
  const viewWerte = useMemo(() => new Set(Object.keys(probe[0]?.values ?? {})), [probe]);
  const berechneteKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const k of [...gezeigt, ...eigeneSort.map((s) => s.key)]) if (katalogMap.get(k)?.berechnet && !viewWerte.has(k)) keys.add(k);
    return [...keys];
  }, [gezeigt, eigeneSort, katalogMap, viewWerte]);
  const trefferIds = useMemo(() => stand.zeilen.map((z) => z.id), [stand.zeilen]);
  const berechnet = useBerechneteWerte(trefferIds, berechneteKeys);

  // --- Berechnungszeile und Limit (Agent 10B, Runde 10) ---
  const typVon = useCallback((k: string) => katalogMap.get(k)?.typ, [katalogMap]);

  const sortWert = useCallback(
    (z: LeadZeile, k: string): unknown => {
      const f = katalogMap.get(k);
      if (k === "status_id") return meta.status(z.status_id)?.position ?? 999;
      if (f?.berechnet && !(z.values && k in z.values)) return berechnet.wert(z.id, k);
      const w = wertDerSpalte(z, k);
      return f?.typ === "nutzer" && typeof w === "string" ? meta.nutzerName(w) : w;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [katalogMap, meta, berechnet.stand],
  );
  const sortTyp = useCallback((k: string) => katalogMap.get(k)?.typ ?? "text", [katalogMap]);

  const sortiert = useMemo(
    () => (eigeneSort.length ? sortiereZeilen(stand.zeilen, eigeneSort, sortWert, sortTyp) : stand.zeilen),
    [stand.zeilen, eigeneSort, sortWert, sortTyp],
  );

  // --- Textsuche in der Liste (Agent 5B): in den geladenen Treffern, Umlaute und Schreibweise egal ---
  const sucheAktiv = suche.trim().length > 0;
  const angezeigt = useMemo(
    () => (sucheAktiv ? sortiert.filter((z) => textTrifft(zeilenText(z, meta.status(z.status_id)?.label), suche)) : sortiert),
    [sortiert, sucheAktiv, suche, meta],
  );
  useEffect(() => setFokus(0), [suche]);
  // Markiert und sichtbar: Massenaktionen treffen nie Leads, die man gerade nicht sieht
  const markierteSichtbar = useMemo(() => {
    const sichtbar = new Set(angezeigt.map((z) => z.id));
    return [...markiert].filter((id) => sichtbar.has(id));
  }, [markiert, angezeigt]);

  // Berechnungszeile: gerechnet wird über alle Treffer der Liste (Server), nicht nur über die geladenen. Mit Suche in der Liste gelten die
  // Treffer der Suche (die Suche läuft in den geladenen Zeilen).
  const sucheIds = useMemo(() => (sucheAktiv ? angezeigt.map((z) => z.id).slice(0, 20_000) : null), [sucheAktiv, angezeigt]);
  const berechnungsZiel = useMemo(
    () =>
      laedt && !stand.zeilen.length
        ? null
        : sucheIds
          ? ({ lead_ids: sucheIds } as const)
          : entwurf
            ? ({ s_query: entwurf.s_query } as const)
            : ({ view_id: viewId } as const),
    [laedt, stand.zeilen.length, sucheIds, entwurf, viewId],
  );
  const berechnung = useListenBerechnung({ gemerkt, typVon, gezeigt, ziel: berechnungsZiel, stempel: ladeStand });
  const gespeicherteAbfrage = stand.view?.s_query ?? null;
  const aktuelleAbfrage: Abfrage | null = entwurf?.s_query ?? gespeicherteAbfrage;
  const limit = limitVon(aktuelleAbfrage);
  const ungekuerzt = useUngekuerzteZahl(aktuelleAbfrage, limit, ladeStand);
  const limitMoeglich = !!aktuelleAbfrage?.query && (stand.view?.type ?? "lead") === "lead";
  const limitSetzen = (n: number | null) => {
    if (!aktuelleAbfrage) return;
    const neu = mitLimit(aktuelleAbfrage, n);
    // wieder wie gespeichert: kein Entwurf mehr (Speichern wird grau)
    setEntwurf(gespeicherteAbfrage && gleicheAbfrage(neu, gespeicherteAbfrage) ? null : { s_query: neu });
  };

  const oeffnen = (z: LeadZeile) => {
    listeSetzen({ quelle: "smartview", titel: name, pfad: `/smart-views/${viewId}`, ids: angezeigt.map((x) => x.id), fokus: z.id });
    navigate(`/lead/${encodeURIComponent(z.id)}`);
  };

  const kopfSort = (key: string) => {
    const typ = katalogMap.get(key)?.typ;
    sortSetzen(sortKlick(wirksameSort, key, typ === "zeit" || typ === "datum" || typ === "zahl"));
  };
  const zelleExtra = (z: LeadZeile, key: string): ReactNode | undefined => {
    const k = kan(key);
    const f = katalogMap.get(k);
    if (!f?.berechnet || (z.values && key in z.values)) return undefined;
    return berechneteZelle(k, f.typ, berechnet.wert(z.id, k), meta);
  };

  const geteilt = stand.view?.is_shared ?? kurz?.is_shared;
  const angeheftet = (alleViews ?? meta.meta?.smart_views ?? []).find((v) => v.id === viewId)?.pinned ?? false;
  const inClose = viewId.startsWith("save_") && !viewId.includes("_crm_");
  const besitzer = stand.view?.owner_id ?? kurz?.owner_id;

  // Nach "Save" oder "Save as" (Agent 5A): neue Smart View öffnen, bei "Save" an derselben Stelle neu laden
  const gespeichert = (v: SmartViewKurz) => {
    if (v.id !== viewId) {
      navigate(`/smart-views/${encodeURIComponent(v.id)}`);
      return;
    }
    ablage.delete(viewId);
    if (entwurfRef.current) setEntwurf(null);
    else void laden(true);
  };

  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <ListenTitel titel={name} aktuell={viewId} />
        {stand.view || kurz ? (
          <span className="liste-kopf__info" title={besitzer ? `Besitzer: ${meta.nutzerName(besitzer)}` : undefined}>
            <Icon name={geteilt ? "link" : "schloss"} groesse={12} />
            {geteilt ? T.smartViews.geteiltMarke : T.smartViews.privat}
            {besitzer && !(besitzer === nutzer?.id || besitzer === nutzer?.close_user_id) ? ` · ${meta.nutzerName(besitzer)}` : ""}
          </span>
        ) : null}
        <div className="liste-kopf__rechts">
          <ListenKopfRechts
            titel={name}
            viewId={viewId}
            // Mit Entwurf oder Suche in der Liste wirken Massenaktionen nur auf die Treffer, die der Nutzer sieht
            ziel={entwurf || sucheAktiv ? { lead_ids: angezeigt.slice(0, 2000).map((z) => z.id) } : { view_id: viewId }}
            trefferZahl={entwurf || sucheAktiv ? angezeigt.length : stand.total}
            markierte={markierteSichtbar}
            onAlleMarkieren={angezeigt.length ? () => setMarkiert(new Set(angezeigt.map((z) => z.id))) : undefined}
            onMarkierungAufheben={() => setMarkiert(new Set())}
            angeheftet={angeheftet}
            onAnheften={() => void anheften(viewId, !angeheftet).catch((e: unknown) => meldung.zeige("fehler", e instanceof Error ? e.message : "Anheften hat nicht geklappt."))}
            filterJson={entwurf?.s_query ?? stand.view?.s_query ?? undefined}
            closeUrl={inClose ? `https://app.close.com/leads/${viewId}/` : null}
            onNeuLaden={() => void laden(true)}
            view={stand.view}
            entwurf={entwurf}
            onGespeichert={gespeichert}
          />
        </div>
      </div>
      {/* Platz 5A: Filterzeile ("› Filters [n]", Bedingungen, Speichern) */}
      <FilterZeile
        art="smartview"
        viewId={viewId}
        view={stand.view}
        trefferZahl={stand.total}
        entwurf={entwurf}
        onEntwurf={setEntwurf}
        onGespeichert={gespeichert}
      />
      {/* Platz 5B: Bedienleiste (Zähler, Sortierung, Spalten, Auswahl, Textsuche) */}
      <Bedienleiste
        liste={liste}
        einheit={{ eins: "Lead", viele: "Leads" }}
        trefferZahl={stand.total}
        geladen={stand.zeilen.length}
        laedt={laedt}
        ladeText={entwurf ? "Treffer werden berechnet …" : "Treffer werden von Close geholt …"}
        spalten={waehlbar}
        gezeigt={gezeigt}
        onSpalten={spaltenSetzen}
        spaltenStandard={standardKeys}
        sortFelder={waehlbar}
        sortierung={eigeneSort}
        onSortierung={sortSetzen}
        sortStandard={ansichtSort}
        sortStandardName="Reihenfolge der Ansicht"
        sortHinweis={stand.hatMehr ? `Sortiert nur die ${stand.zeilen.length.toLocaleString("de-DE")} geladenen Treffer.` : null}
        suche={{ wert: suche, onAendern: setSuche, platzhalter: "In der Liste suchen …" }}
        auswahl={{
          anzahl: markierteSichtbar.length,
          onAlleMarkieren: angezeigt.length ? () => setMarkiert(new Set(angezeigt.map((z) => z.id))) : undefined,
          onAufheben: () => setMarkiert(new Set()),
        }}
        hinweis={
          <>
            <LimitSteuerung
              limit={limit}
              gesamt={ungekuerzt}
              reihenfolge={sortBeschreibung(ansichtSort, (k) => waehlbarMap.get(k)?.label ?? k)}
              onSetzen={limitSetzen}
              moeglich={limitMoeglich}
            />
            {sucheAktiv ? (
            <span className="liste-leiste__hinweis">
              {angezeigt.length.toLocaleString("de-DE")} von {stand.zeilen.length.toLocaleString("de-DE")} geladenen Treffern
              {stand.hatMehr ? " (nur geladene Treffer durchsucht)" : ""}
            </span>
            ) : null}
          </>
        }
        warnung={
          stand.fehlend.length ? (
            <span className="liste-leiste__warnung" title={stand.fehlend.join(", ")}>
              <Icon name="warnung" groesse={13} /> {stand.fehlend.length} Treffer fehlen noch in der lokalen Kopie
            </span>
          ) : null
        }
      />
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void laden()} /> : null}
      {laedt && !stand.zeilen.length ? (
        <Laden text="Treffer werden von Close geholt …" />
      ) : !fehler && !stand.zeilen.length ? (
        <Leer titel="Keine Leads in dieser Smart View." text="Close liefert für diese Ansicht gerade keine Treffer." />
      ) : stand.zeilen.length && !angezeigt.length ? (
        <Leer titel={`Kein Treffer für „${suche.trim()}“.`} text={stand.hatMehr ? "Gesucht wird in den geladenen Treffern. Weitere lädt der Knopf unter der Liste." : "Anderen Suchbegriff versuchen."}>
          <button type="button" className="knopf" onClick={() => setSuche("")}>
            Suche leeren
          </button>
        </Leer>
      ) : stand.zeilen.length ? (
        <LeadTabelle
          klasse={spalten.filter((s) => s.key !== KONTAKTWEGE).length >= 6 ? "tabelle--dicht" : undefined}
          spalten={spaltenMitAuswahl}
          zeilen={angezeigt}
          fokus={Math.min(fokus, angezeigt.length - 1)}
          setFokus={setFokus}
          onOeffnen={(z) => oeffnen(z)}
          sort={wirksameSort[0] ? { key: wirksameSort[0].key, ab: wirksameSort[0].ab } : null}
          onSort={kopfSort}
          auswahl={{ markiert, onAendern: setMarkiert }}
          breiten={layout.breiten}
          onBreite={layout.onBreite}
          onVerschieben={layout.onVerschieben}
          berechnung={(s) => (
            <BerechnungsZelle
              label={s.label || (waehlbarMap.get(kan(s.key))?.label ?? s.key)}
              typ={typVon(kan(s.key))}
              funktion={berechnung.wahl[kan(s.key)]}
              ergebnis={berechnung.ergebnisse[kan(s.key)]}
              laedt={berechnung.laedt}
              anzahl={berechnung.anzahl}
              gekappt={berechnung.gekappt}
              onWaehlen={(f) => berechnung.setzen(kan(s.key), f)}
            />
          )}
          onEnde={stand.hatMehr && stand.zeilen.length >= AUTOMATISCH_BIS ? () => void mehrLaden() : undefined}
          zelle={(z, s) =>
            leadZelle(
              z,
              s.key,
              meta,
              (e: MouseEvent<HTMLAnchorElement>) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                oeffnen(z);
              },
              (key) => zelleExtra(z, key),
            )
          }
          fuss={
            stand.hatMehr ? (
              <div className="tabelle__mehr">
                <button type="button" className="knopf" disabled={laedtMehr} onClick={() => void mehrLaden()}>
                  {laedtMehr ? "Lädt …" : "Weitere Treffer laden"}
                </button>
              </div>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
